// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core'
import * as React from 'react'
import Modal from 'react-bootstrap/lib/Modal'
import Glyphicon from 'react-bootstrap/lib/Glyphicon'
import { Mutation, Query } from 'react-apollo'
import moment from 'moment'

import type { CommonActionProps } from './types.actions'
import type { ActionStatus, GridItem } from '../../types'

import ToggleController from '../../controllers/ToggleController'
import StatusController from '../../controllers/StatusController'
import ButtonAction from './ButtonAction'
import Prompt from '../Prompt'
import Loading from '../Loading'
import { activateOrderMutation } from '../../graphql/mutations/orders/activateOrder.mutation'
import getMenuItems from '../../graphql/queries/getMenuItems.queries'
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query'
import messages from '../../config/messages'
import { getDateFormat } from '../../utils'
import moduleDataQuery from '../../graphql/queries/inventoryData.query'
import Base from '../../layout/Base.layout'
import { DesktopView, MobileView } from '../../config/mediaQuery'

type Props = {
  onOrderActivated: (ActionStatus) => typeof undefined,
  orderActiveStatus?: ActionStatus,
  onMutationCompleted: () => mixed,
  item: GridItem,
}

type State = {
  activating: boolean,
}

class ActivateOrderAction extends React.Component<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
  }

  state = {
    activating: false,
  }

  activatingTimerId: TimeoutID

  onMutationCompleted = (data: any) => {
    if (data.activateOrder && data.activateOrder.error) {
      return
    }

    if (this.activatingTimerId) {
      clearTimeout(this.activatingTimerId)
    }

    this.activatingTimerId = setTimeout(() => {
      this.setState({
        activating: false,
      })
    }, 500)

    this.props.onOrderActivated('completed')

    this.props.onMutationCompleted()
  }

  componentWillUnmount = () => {
    if (this.activatingTimerId) {
      clearTimeout(this.activatingTimerId)
    }
  }

  onStartActivating = ({ mutation, onToggleVisibility }: any) => async () => {
    this.setState({
      activating: true,
    })

    // When Saved Order has items inside — this order doesn't have id and has OrderNo
    // When Saved Order doesn't contain items — this order has id and does not have OrderNo
    const savedOrderHasDet = this.props.item.id === null

    try {
      if (savedOrderHasDet) {
        const inventoryModuleArray = await this.getLatestInventory(this.props.item)
        const selectedOrderDet = this.props.item.Det
        const customDetToActivate = this.changeAvailableQtyInSavedOrder(inventoryModuleArray, selectedOrderDet)

        await mutation({
          variables: {
            input: {
              id: this.props.item.id,
              Po: this.props.item.Po,
              Coop: this.props.item.Coop,
              Note: this.props.item.Note,
              OrderNo: this.props.item.OrderNo,
              Account: this.props.item.Account,
              Det: customDetToActivate,
              OrderType: this.props.item.OrderTypeRaw,
              OrderDate:
                this.props.item.DateShip &&
                moment(this.props.item.DateShip).format(getDateFormat('server')),
            },
          },
        })
      } else {
        await mutation({
          variables: {
            input: {
              id: this.props.item.id,
              Po: this.props.item.Po,
              Coop: this.props.item.Coop,
              Note: this.props.item.Note,
              OrderNo: this.props.item.OrderNo,
              Account: this.props.item.Account,
              Det: this.props.item.Det,
              OrderType: this.props.item.OrderTypeRaw,
              OrderDate:
                this.props.item.DateShip &&
                moment(this.props.item.DateShip).format(getDateFormat('server')),
            },
          },
        })
      }


      if (typeof onToggleVisibility === 'function') {
        onToggleVisibility()
      }
    } catch (e) {
      // TODO: handle error
    }
  }

  onExit = (options: { onToggleVisibility: () => mixed, loading: boolean }) => {
    const { onToggleVisibility, loading } = options

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return

    onToggleVisibility()
  }

  // Checks what currently in inventory, and if Det Qty of every item of selected order
  // to activate needs to be reduced.
  // Qty will be reduced only after we press activate and
  // confirm (note, it happens NOT before activation).
  changeAvailableQtyInSavedOrder = (
    latestInventoryArray,
    savedOrderDetArray,
  ) => {
    return savedOrderDetArray.map((det) => {
      const latestProductAvailable = latestInventoryArray.find((product) => product.Code === det.Code)

      if (latestProductAvailable) {
        const latestProductAvailableQty = latestProductAvailable.QtyA
        const currentProductQty = det.Qty

        if (currentProductQty > latestProductAvailableQty) {
          return {
            ...det,
            Qty: latestProductAvailableQty,
          }
        } else {
          return {
            ...det
          }
        }
      }

      return {
        ...det,
      }
    })
  }

  getLatestInventory = async (selectedOrder) => {
    const inventoryData = await Base.getClient().query({
      query: moduleDataQuery,
      variables: {
        state: selectedOrder.Account.Level,
        selectedOrderType: '',
        selectedOrderDate: '',
      },
      fetchPolicy: 'network-only'
    })

    return inventoryData.data.inventoryContent
  }

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => (
          <Query
            query={getActiveOrderQuery}
            variables={{
              selectedRow: this.props.item,
            }}
          >
            {({ data, loading: contentLoading }: any) => {
              const activeItem = this.props.item || {}
              const activeOrder =
                data && data.activeOrder ? data.activeOrder : {}
              const hasActiveOrder =
                Object.keys(activeOrder).length > 0 &&
                (activeOrder.id !== null || activeOrder.OrderNo !== null)

              const isCurrentRowActiveOrder =
                (activeItem.id === activeOrder.id && activeOrder.id !== null) ||
                (activeItem.OrderNo === activeOrder.OrderNo &&
                  activeOrder.OrderNo !== null)

              if (this.props.item) {
                return (
                  <Mutation
                    mutation={activateOrderMutation}
                    onCompleted={this.onMutationCompleted}
                    refetchQueries={() => {
                      return [
                        {
                          query: getMenuItems,
                        },
                        {
                          query: getActiveOrderQuery,
                        },
                      ]
                    }}
                    skip={!visible}
                  >
                    {(mutation, { loading, error }) => {
                      // If there is an active order, we are gonna show a prompt asking if they
                      // are sure they want to move on with the activation. If there isn't, we
                      // are just gonna go ahead and active the selected order.
                      const actionHandler = hasActiveOrder
                        ? onToggleVisibility
                        : this.onStartActivating({
                          mutation,
                        })

                      return (
                        <StatusController hasError={!!error} loading={loading}>
                          {({ status }) => {
                            return (
                              <React.Fragment>
                                <DesktopView>
                                  <ButtonAction
                                    bsSize={this.props.size}
                                    disabled={
                                      this.props.disabled ||
                                      isCurrentRowActiveOrder ||
                                      loading ||
                                      contentLoading ||
                                      this.state.activating
                                    }
                                    onClick={actionHandler}
                                  >
                                    {/*
                                    When we don't have currently active order,
                                    we won't see modal window, which will show us loading process,
                                    but in order to let user understand, that the process of activation goes
                                    we will have this loading wrapped in div, as grid overlaps
                                  */}
                                    {!hasActiveOrder && this.state.activating && (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '20px',
                                        }}
                                      >
                                        <Loading />
                                      </div>
                                    )}
                                    <Glyphicon glyph="check" />{' '}
                                    <span>
                                      {(status === '' || status === 'error') &&
                                        'Activate Order'}
                                      {status === 'loading' &&
                                        'Activating the order...'}
                                      {status === 'completed' &&
                                        'Order Activated!'}
                                    </span>
                                  </ButtonAction>
                                </DesktopView>
                                <MobileView>
                                  <button
                                    // disabled={}
                                    onClick={actionHandler}
                                  >
                                    <img src='assets/activate.svg' alt='activate icon' />
                                    {(this.state.activating && 'Activating the order...') || (status === 'completed' &&
                                      'Order Activated!') || 'Activate Order'}
                                  </button>

                                </MobileView>
                                {visible && (
                                  <MobileView>
                                    {(isMobile) => (
                                      <Modal
                                        show={visible}
                                        onHide={() =>
                                          this.onExit({
                                            onToggleVisibility,
                                            loading,
                                          })
                                        }
                                        className="modal--form"
                                        dialogClassName={isMobile ? 'confirmation-modal' : ''}
                                      >
                                        <Modal.Header closeButton={!isMobile}>
                                          <Modal.Title>Activate Order</Modal.Title>
                                        </Modal.Header>

                                        {contentLoading ? (
                                          <Modal.Body>
                                            <Loading />
                                          </Modal.Body>
                                        ) : (
                                          <Modal.Body
                                            css={{
                                              ...(!isMobile && { minHeight: '300px' }),
                                            }}
                                          >
                                            {this.state.activating && !isMobile && <Loading />}
                                            <Prompt
                                              onCancel={() =>
                                                this.onExit({
                                                  onToggleVisibility,
                                                  loading,
                                                })
                                              }
                                              onConfirm={this.onStartActivating({
                                                onToggleVisibility,
                                                mutation,
                                              })}
                                              message={messages.activeOrder.prompt}
                                              confirmText={!isMobile ? 'Confirm' : (this.state.activating && 'Activating the order...') || (status === 'completed' &&
                                              'Order Activated!') || 'Confirm'}
                                            />
                                          </Modal.Body>
                                        )}
                                      </Modal>
                                    )}

                                  </MobileView>
                                )}
                              </React.Fragment>
                            )
                          }}
                        </StatusController>
                      )
                    }}
                  </Mutation>
                )
              }

              return null
            }}
          </Query>
        )}
      </ToggleController>
    )
  }
}

export default ActivateOrderAction
